import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import ViewListIcon from '@mui/icons-material/ViewList';
import {
    alpha,
    AppBar,
    Badge,
    Box,
    Button,
    Divider,
    Drawer,
    IconButton,
    List, ListItem,
    ListItemButton,
    ListItemText,
    Toolbar,
    Tooltip,
    Typography,
    useTheme
} from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import { ForwardedRef, forwardRef, useEffect, useRef, useState } from 'react';
import iconMap from '../../../paginas/gestao/menu/iconmap';
import { useEasyBrowserContext } from '../browser';
import FindTaskBar from './findtaskbar';
import ItemTask from './itemtask';
import MenuPrincipalTaskBar from './menuprincipal/menuprincipaltaskbar';
import UsuarioMenu from './menusuario';
import { ZLayers } from '../zlayes';
import NotificacaoMenu from '../NotificacaoMenu';
import ServerStatus from '../Serverstatus';

const TaskBar = forwardRef((_props: unknown, _ref: ForwardedRef<any>) => {
    const { listaTasks, fecharPagina, fecharTodasPaginas, abrirPagina, taskSelecionada } = useEasyBrowserContext()!;
    const theme   = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [drawerOpen, setDrawerOpen] = useState(false);

    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const itemRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

    const tasksAtivas = listaTasks.filter(item => item.id !== "easy.schedule.menu.principal");

    // Rola até a aba selecionada
    useEffect(() => {
        if (taskSelecionada && itemRefs.current[taskSelecionada.id]) {
            itemRefs.current[taskSelecionada.id]?.scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
                inline: 'center',
            });
        }
    }, [taskSelecionada]);

    const handleScroll = (direction: 'left' | 'right') => {
        scrollContainerRef.current?.scrollBy({
            left: direction === 'left' ? -300 : 300,
            behavior: 'smooth',
        });
    };

    return (
        <AppBar
            position="static"
            className='no-print'
            sx={{
                bgcolor: theme.palette.background.paper,
                borderBottom: `1px solid ${theme.palette.divider}`,
                boxShadow: 'none',
                minHeight: '48px',
                // ZLayers centraliza o valor — garante que a TaskBar fique
                // acima de qualquer conteúdo de página sem usar ZindexUtils
                zIndex: ZLayers.global.taskBar,
            }}
        >
            <Toolbar variant="dense" sx={{ px: 1, gap: 0.5, minHeight: '48px' }}>
                <MenuPrincipalTaskBar />
                <Divider orientation="vertical" variant="middle" flexItem sx={{ mx: 0.5 }} />

                {!isMobile ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1, overflow: 'hidden' }}>
                        <Tooltip title="Todas as janelas">
                            <IconButton size="small" onClick={() => setDrawerOpen(true)} sx={{ mr: 0.5, mt: 0.8 }}>
                                <Badge badgeContent={tasksAtivas.length} color="primary">
                                    <ViewListIcon fontSize="small" />
                                </Badge>
                            </IconButton>
                        </Tooltip>

                        <IconButton size="small" onClick={() => handleScroll('left')}>
                            <ChevronLeftIcon fontSize="small" />
                        </IconButton>

                        <Box
                            ref={scrollContainerRef}
                            sx={{
                                display: 'flex',
                                gap: 0.2,
                                flexGrow: 1,
                                overflowX: 'auto',
                                alignItems: 'center',
                                '&::-webkit-scrollbar': { display: 'none' },
                                msOverflowStyle: 'none',
                                scrollbarWidth: 'none',
                            }}
                        >
                            {tasksAtivas.map((item) => (
                                <Box
                                    key={item.id}
                                    ref={(el: any) => itemRefs.current[item.id] = el}
                                    sx={{ display: 'flex' }}
                                >
                                    <ItemTask itemMenu={item} />
                                </Box>
                            ))}
                        </Box>

                        <IconButton size="small" onClick={() => handleScroll('right')}>
                            <ChevronRightIcon fontSize="small" />
                        </IconButton>
                    </Box>
                ) : (
                    <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'center' }}>
                        <IconButton onClick={() => setDrawerOpen(true)} color="primary">
                            <Badge badgeContent={tasksAtivas.length} color="error">
                                <ViewListIcon />
                            </Badge>
                        </IconButton>
                    </Box>
                )}

                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, ml: 'auto', flexShrink: 0 }}>
                    <FindTaskBar />
                    <NotificacaoMenu/>
                     <ServerStatus />
                    <UsuarioMenu />
                    
                </Box>
            </Toolbar>

            {/* Drawer de janelas abertas */}
            <Drawer
                anchor={isMobile ? "bottom" : "right"}
                open={drawerOpen}
                onClose={() => setDrawerOpen(false)}
                PaperProps={{
                    sx: {
                        width: isMobile ? '100%' : 350,
                        borderRadius: isMobile ? '16px 16px 0 0' : '12px 0 0 12px',
                        // Drawer também precisa ficar acima das páginas
                        zIndex: ZLayers.global.taskBarDropdown,
                    }
                }}
            >
                <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', height: '100%' }}>
                    <Typography variant="h6" sx={{ fontWeight: 800, mb: 2, color: theme.palette.primary.main }}>
                        Janelas Abertas
                    </Typography>
                    <Divider sx={{ mb: 1 }} />
                    {tasksAtivas.length > 0 && (
                        <Button
                            variant="outlined"
                            color="error"
                            size="small"
                            startIcon={<CloseIcon fontSize="small" />}
                            fullWidth
                            onClick={() => { fecharTodasPaginas(); setDrawerOpen(false); }}
                            sx={{ mb: 1 }}
                        >
                            Fechar todas
                        </Button>
                    )}
                    <List sx={{ flexGrow: 1, overflowY: 'auto' }}>
                        {tasksAtivas.map((item) => (
                            <ListItem
                                key={item.id}
                                secondaryAction={
                                    <IconButton edge="end" onClick={() => fecharPagina(item)} color="error" size="small">
                                        <CloseIcon fontSize="small" />
                                    </IconButton>
                                }
                                disablePadding
                                sx={{
                                    mb: 1,
                                    borderRadius: 2,
                                    border: '1px solid',
                                    borderColor: taskSelecionada?.id === item.id
                                        ? theme.palette.primary.main
                                        : theme.palette.divider,
                                    bgcolor: taskSelecionada?.id === item.id
                                        ? alpha(theme.palette.primary.main, 0.05)
                                        : 'transparent',
                                }}
                            >
                                <ListItemButton onClick={() => { abrirPagina(item); setDrawerOpen(false); }}>
                                    <Box sx={{ mr: 2, color: taskSelecionada?.id === item.id ? theme.palette.primary.main : 'text.secondary' }}>
                                        {item.icone ? iconMap[item.icone] : iconMap["default"]}
                                    </Box>
                                    <ListItemText
                                        primary={item.descricao}
                                        primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                                    />
                                </ListItemButton>
                            </ListItem>
                        ))}
                    </List>
                </Box>
            </Drawer>
        </AppBar>
    );
});

TaskBar.displayName = 'TaskBar';
export default TaskBar;